import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { sanitizeCanonical } from '~/utils/seo'
import { useRuntimeConfig } from '#app/nuxt'
import { useRoute } from '#app/composables/router'

import type { SbListingPage } from '~/cms/storyblok/types/storyblok.gen'
import type { SEOTag } from '../../types/seo'
import {
  hasSuppressingQuery,
  pickXDefaultHref,
  stripHreflangTrailingSlash,
} from '../../utils/hreflang'

import useCms from '~/composables/cms/useCms'
import useCategoryAlternateNames from '../../composables/useCategoryAlternateNames'
import { parsePageParam } from '../../utils/pagination'

type PlpState = ReturnType<typeof useNewPlp>

export default (plp?: PlpState) => {
  const i18n = useI18n()
  const route = useRoute()
  const config = useRuntimeConfig()

  const plpPath = computed(() =>
    getPlpPath(route.params.slug as string | string[]),
  )
  const categoryAlternateNamesParams = computed(() => ({
    category: plpPath.value,
  }))

  const key = `usePlp-${plpPath.value}`
  const cms = useCms<SbListingPage>(`useCms-${key}`)

  const { data: categoryAlternateNames, refresh: fetchCategoryAlternateNames } =
    useCategoryAlternateNames({
      params: categoryAlternateNamesParams,
      options: { immediate: false },
      key: `useCategoryAlternateNames-${plpPath.value}`,
    })

  const { selectedCategory } = plp ?? useNewPlp()

  // +-----+
  // | SEO |
  // +-----+
  // await fetchCategoryAlternateNames()
  const fetch = async () => {
    await fetchCategoryAlternateNames()
  }

  // Sourced from the URL, not from client-fetched pagination state, so the
  // canonical/hreflang page value is identical for every user-agent during SSR
  // (ECOM-2635 — the crawler path fetches products server-side while the human
  // path fetches them client-side, so pagination state is UA-dependent during
  // SSR; route.query is not). See parsePageParam.
  const page = computed(() => parsePageParam(route.query.page))
  const hasPageQuery = computed(() => !!route.query.page)
  // Canonical/hreflang must never carry ?page=1 — it's a noindex duplicate of
  // the canonical listing URL (ECOM-1495). Only pages 2+ get the page param,
  // mirroring paginationLinkTo() in useRouteHelpers. Gating on page > 1 (not
  // hasPageQuery) means an explicit ?page=1 URL resolves to the bare path.
  const isPaginated = computed(() => page.value > 1)

  const cleanUpTrailingQuestionMark = (url: string) =>
    url.endsWith('?') ? url.slice(0, -1) : url

  const appendPageParam = (url: string, paginationValue: number): string => {
    const parsedUrl = new URL(url)
    parsedUrl.searchParams.set('page', `${paginationValue}`)
    return parsedUrl.toString()
  }

  const metaDataFallbacks = computed(() => ({
    title:
      selectedCategory.value && selectedCategory.value?.name
        ? `${selectedCategory.value?.name} ${i18n.t('global.title_category')}`
        : undefined,
    description: i18n.t('plp.seo_description', {
      categoryName: selectedCategory.value?.name,
      gender: 'women',
    }),
  }))

  const metaDataOverrides = computed(() => {
    return {
      robots:
        cms.data.value?.content?.noIndex || hasPageQuery.value
          ? 'noindex,follow'
          : 'index,follow',
      canonical: 'skip',
    }
  })

  const metaDataExtensions = computed(() => {
    const alternateNames = categoryAlternateNames.value as
      | Record<string, string>
      | null
      | undefined
    if (!alternateNames) {
      return { link: [] }
    }
    const canonicalUrl = new URL(`${config.public.baseUrl}${route.path}`)
    const sanitizedCanonicalUrl = sanitizeCanonical(`${canonicalUrl}`)
    const sanitizedCanonicalUrlWithPage = appendPageParam(
      sanitizedCanonicalUrl,
      page.value,
    )

    const currentIso = i18n.localeProperties.value.iso
    const canonicalHref = cleanUpTrailingQuestionMark(
      isPaginated.value ? sanitizedCanonicalUrlWithPage : sanitizedCanonicalUrl,
    )

    const canonicalTag: SEOTag = {
      rel: 'canonical',
      href: canonicalHref,
    }

    // Suppress all hreflang when the URL carries a filter/sort param (any query
    // key other than `page`): such a view is not a canonical hreflang group
    // member (ECOM-2433). Canonical still points at the clean listing URL above,
    // and pagination (`?page=N`) keeps its group (ECOM-1495).
    if (hasSuppressingQuery(route.query)) {
      return { link: [canonicalTag] }
    }

    const withPageQuery = (href?: string) =>
      isPaginated.value ? appendPageParam(href || '', page.value) : href || ''
    const normalizeHref = (href?: string) =>
      stripHreflangTrailingSlash(withPageQuery(href))

    const values: SEOTag[] = [
      // Explicitly add the current locale version
      {
        rel: 'alternate',
        hreflang: typeof currentIso === 'string' ? currentIso : undefined,
        href: stripHreflangTrailingSlash(canonicalHref),
      },
      // Alternate links from categoryAlternateNames
      ...Object.entries(alternateNames)
        .sort(([a], [b]) => a.localeCompare(b)) // sort them so they're always in the same order
        .map(([key, href]) => ({
          rel: 'alternate',
          hreflang: key,
          href: normalizeHref(href),
        })),
    ]

    // x-default = the de-DE member verbatim (the self-reference already carries
    // it when the current page is German); omit when no German member exists
    // rather than synthesizing a /de path.
    const xDefaultHref = pickXDefaultHref(
      values.map((tag) => ({ hreflang: tag.hreflang ?? '', href: tag.href })),
    )
    if (xDefaultHref) {
      values.push({
        rel: 'alternate',
        hreflang: 'x-default',
        href: xDefaultHref,
      })
    }

    values.push(canonicalTag)

    // rel=next/prev intentionally omitted: Google deprecated them as an
    // indexing signal in 2019, and they re-introduced the ?page=1 URL (ECOM-1495).

    return { link: values }
  })

  const storyMetaData = computed(() => ({
    data: cms.data,
    fallbacks: metaDataFallbacks,
    extensions: metaDataExtensions,
    overrides: metaDataOverrides,
  }))

  return {
    storyMetaData,
    categoryAlternateNames,
    fetch,
  }
}
